import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import AppDateInput from '../../../components/AppDateInput'
import AppDropdown from '../../../components/AppDropdown'
import { useCompanyEntryScreen } from './hooks/useCompanyEntryScreen'
import { appToast } from '../../../components/AppToast'
import { listCurrencies } from '../currency/api/currencyApi'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function Field({ label, value, onChange, placeholder, required = false, type = 'text', className = '' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant ${className}`}
      />
    </label>
  )
}

export default function CompanyMasterEntryScreen() {
  const { state, actions } = useCompanyEntryScreen()

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-8">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-on-surface-variant">Administrative Master</p>
          <h1 className="mt-1 text-4xl font-extrabold tracking-[-0.02em] text-primary">Company Master</h1>
        </div>

        <div className="flex items-center gap-3">
          {!state.pendingApproval && (
            <Link
              to={state.returnTo || '/masters/company/list'}
              className={`inline-flex items-center justify-center rounded-md bg-secondary-container px-7 py-3 text-sm font-semibold text-on-secondary-container ${
                state.isSaving ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              Cancel
            </Link>
          )}
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || Boolean(state.pendingApproval)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-8 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">
              {state.pendingApproval ? 'task_alt' : 'save'}
            </span>
            {state.pendingApproval ? 'Submitted ✓' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </section>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} queueRoute="/approval/masters/list?module=company" />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-14 text-center text-sm text-on-surface-variant">
          Loading company details...
        </div>
      ) : (
        <section className="grid grid-cols-1 gap-8 xl:grid-cols-[1.75fr_0.85fr]">
          <div className="space-y-8">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <h2 className="text-3xl font-bold text-primary">Entity Configuration</h2>

              <div className="mt-8 space-y-8">
                <div>
                  <Field
                    label="Company Name"
                    required
                    value={state.form.name}
                    onChange={(value) => actions.patchForm('name', value)}
                    placeholder="e.g. Acme Corporation Pvt Ltd"
                  />
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-on-surface-variant/80">
                    <span className="material-symbols-outlined text-base">info</span>
                    This name will appear on all tax invoices and legal documents.
                  </p>
                </div>

                <Field
                  label="GST Number"
                  value={state.form.gst_tax_number}
                  onChange={(value) => actions.patchForm('gst_tax_number', value.toUpperCase())}
                  placeholder="22AAAAA0000A1Z5"
                  className="uppercase tracking-[0.07em]"
                />

                <div className="grid gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                    Reporting Currency <span className="text-error">*</span>
                  </span>
                  <AppDropdown
                    value={state.form.currency}
                    onChange={(value) => actions.patchForm('currency', value)}
                    fetchOptions={async (search, page, pageSize) => {
                      const list = await listCurrencies()
                      const items = list
                        .filter((c) => c.isActive === 1 || c.is_active === 1)
                        .map((c) => ({
                          value: c.currencyCode || c.currency_code,
                          label: `${c.currencyCode || c.currency_code} — ${c.currencyName || c.currency_name}`,
                        }))
                      return { items, hasMore: false }
                    }}
                    placeholder="Select reporting currency"
                    triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-4 py-3 text-sm text-on-surface"
                    initialLabel={state.form.currency}
                  />
                  <p className="flex items-center gap-1 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-sm">info</span>
                    All dashboard financials will be displayed in this currency.
                  </p>
                </div>
              </div>
            </article>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-low p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="rounded-md bg-surface-container-lowest p-2 text-primary">
                    <span className="material-symbols-outlined">language</span>
                  </div>
                  <h3 className="text-xl font-bold text-on-surface">Digital Presence</h3>
                </div>

                <div className="space-y-4">
                  <Field
                    label="Company Website"
                    value={state.form.company_website}
                    onChange={(value) => actions.patchForm('company_website', value)}
                    placeholder="https://example.com"
                    type="url"
                    className="bg-surface-container-lowest"
                  />
                  <Field
                    label="Corporate Email"
                    value={state.form.corporate_email}
                    onChange={(value) => actions.patchForm('corporate_email', value)}
                    placeholder="contact@company.com"
                    type="email"
                    className="bg-surface-container-lowest"
                  />
                </div>
              </article>

              <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-low p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="rounded-md bg-surface-container-lowest p-2 text-primary">
                    <span className="material-symbols-outlined">call</span>
                  </div>
                  <h3 className="text-xl font-bold text-on-surface">Contact Channels</h3>
                </div>

                <div className="space-y-4">
                  <Field
                    label="Support Phone"
                    value={state.form.support_phone}
                    onChange={(value) => actions.patchForm('support_phone', value)}
                    placeholder="+91 9876543210"
                    className="bg-surface-container-lowest"
                  />
                  <Field
                    label="Fax Number"
                    value={state.form.fax_number}
                    onChange={(value) => actions.patchForm('fax_number', value)}
                    placeholder="+91 1234567890"
                    className="bg-surface-container-lowest"
                  />
                </div>
              </article>
            </div>

            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <h2 className="text-3xl font-bold text-primary">Work Calendar & Holidays</h2>
              <p className="mt-1 text-sm text-on-surface-variant">
                Configure the company's weekly off-days and public holidays. These settings define resource capacity and availability.
              </p>

              {/* Weekend Days Selector */}
              <div className="mt-8 border-b border-outline-variant/10 pb-8">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant block mb-3">
                  Weekend Days (Weekly Off-Days)
                </span>
                <div className="flex flex-wrap gap-2.5">
                  {DAYS_OF_WEEK.map((day) => {
                    const isSelected = (state.form.weekend_days || []).includes(day)
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => actions.toggleWeekendDay(day)}
                        className={`rounded-full px-5 py-2.5 text-xs font-bold border transition-all ${
                          isSelected
                            ? 'bg-primary border-primary text-on-primary shadow-lg shadow-primary/10'
                            : 'border-outline-variant/30 bg-surface-container-low text-on-surface hover:bg-surface-container-high'
                        }`}
                      >
                        {day}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Public & Company Holidays List */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                      Public & Company Holidays
                    </span>
                    <p className="text-xs text-on-surface-variant mt-0.5">
                      {(state.form.holidays || []).length} holiday{((state.form.holidays || []).length !== 1) ? 's' : ''} configured
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {(state.form.holidays || []).map((holiday) => (
                    <div key={holiday.id} className="flex flex-col gap-4 rounded-2xl border border-outline-variant/20 bg-surface-container-low p-4 sm:flex-row sm:items-center">
                      {/* Holiday Name */}
                      <div className="flex-1">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                          Holiday Name <span className="normal-case font-normal text-on-surface-variant/70">(Optional)</span>
                        </label>
                        <input
                          type="text"
                          value={holiday.name}
                          onChange={(e) => actions.updateHoliday(holiday.id, 'name', e.target.value)}
                          placeholder="e.g. Christmas"
                          className="w-full rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface placeholder-on-surface-variant/40 outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      
                      {/* From Date */}
                      <div className="w-full sm:w-[160px]">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                          From Date {holiday.name?.trim() ? <span className="text-error">*</span> : null}
                        </label>
                        <AppDateInput
                          value={holiday.from_date}
                          onChange={(val) => actions.updateHoliday(holiday.id, 'from_date', val)}
                          placeholder="dd-mm-yyyy"
                          inputClassName="w-full rounded-xl border-0 bg-surface-container-lowest px-3 py-3 text-sm text-on-surface shadow-none"
                        />
                      </div>

                      {/* To Date */}
                      <div className="w-full sm:w-[160px]">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
                          To Date (Optional)
                        </label>
                        <AppDateInput
                          value={holiday.to_date}
                          onChange={(val) => actions.updateHoliday(holiday.id, 'to_date', val)}
                          placeholder="dd-mm-yyyy"
                          inputClassName="w-full rounded-xl border-0 bg-surface-container-lowest px-3 py-3 text-sm text-on-surface shadow-none"
                        />
                      </div>

                      {/* Delete button */}
                      <div className="self-end sm:self-center pt-2 sm:pt-4">
                        <button
                          type="button"
                          onClick={() => actions.removeHoliday(holiday.id)}
                          title="Remove holiday"
                          className="flex h-10 w-10 items-center justify-center rounded-xl text-on-surface-variant/40 transition-colors hover:bg-red-50 hover:text-red-500"
                        >
                          <span className="material-symbols-outlined text-base">delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Holiday Button */}
                <button
                  type="button"
                  onClick={actions.addHoliday}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/40 py-3.5 text-sm font-semibold text-on-surface-variant/70 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="material-symbols-outlined text-base">add</span>
                  Add Holiday
                </button>
              </div>
            </article>
          </div>

          <div className="space-y-8">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <h3 className="text-2xl font-bold text-primary">Account Lifecycle</h3>

              <div className="mt-6 rounded-md bg-surface-container-low p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xl font-bold text-on-surface">Active Status</p>
                    <p className="text-xs text-on-surface-variant">Enables system access</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => actions.patchForm('is_archive', state.form.is_archive ? 0 : 1)}
                    className={`relative h-8 w-14 rounded-full px-1 transition-colors ${
                      state.form.is_archive ? 'bg-slate-300' : 'bg-primary'
                    }`}
                    aria-label="Toggle active status"
                  >
                    <span
                      className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${
                        state.form.is_archive ? 'left-1' : 'left-7'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="mt-6 rounded-md border border-dashed border-outline-variant bg-surface-container-lowest p-4 text-sm text-on-surface-variant">
                Setting a company to <span className="font-bold text-error">Inactive</span> will suspend all child
                branch activities and department workflows immediately.
              </div>
            </article>

            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <h3 className="text-2xl font-bold text-primary">Visual Identity</h3>
              <label className="mt-6 block cursor-pointer">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(event) => actions.handleLogoFileChange(event.target.files?.[0] || null)}
                />

                {state.logoPreviewUrl ? (
                  <img
                    src={state.logoPreviewUrl}
                    alt="Company logo preview"
                    className="h-[260px] w-full rounded-md border-2 border-dashed border-outline-variant object-cover"
                  />
                ) : (
                  <div className="flex h-[260px] w-full flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-outline-variant bg-surface-container-low text-center">
                    <span className="material-symbols-outlined text-5xl text-outline">add_photo_alternate</span>
                    <p className="text-lg font-bold text-on-surface">Upload Logo</p>
                    <p className="text-xs text-on-surface-variant">PNG, JPG up to 5MB</p>
                  </div>
                )}
              </label>
            </article>

            <article className="rounded-[2rem] bg-primary/5 p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-[0.14em] text-primary">Setup Progress</p>
                <p className="text-sm font-bold text-primary">{state.completionPercent}%</p>
              </div>
              <div className="mt-3 h-2.5 w-full rounded-full bg-surface-variant">
                <div className="h-full rounded-full bg-primary" style={{ width: `${state.completionPercent}%` }} />
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  )
}
